import { Injectable, Injector } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { MenuEntryType, MenuStructure } from '@nimbus-library/features/menu';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import { IMenuProviderService } from '@nimbus-library/features/menu/services/contracts/i-menu-provider.service';
import { MenuViewModel } from '@nimbus-library/features/menu/models/menu.view-model';
import { AuthenticationFilter } from '@nimbus-global/features/menu/services/filters/authentication.filter';
import { MenuMapper } from '@nimbus-library/features/menu/services/mapper/nimbus-menu.mapper';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { Routes } from '@nimbus-admin/features/menu/models/consts/routes.const';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { RoleFilter } from '@nimbus-global/features/menu/services/filters/role.filter';
import { PermissionFilter } from '@nimbus-global/features/menu/services/filters/permission.filter';
import { ExcludePermissionFilter } from '@nimbus-global/features/menu/services/filters/exclude-permission.filter';
import { NavigationFilter } from '@nimbus-library/features/menu/services/filters/navigation.filter';
import { CombineFilter } from '@nimbus-global/features/menu/services/filters/combine.filter';
import { FeatureFlippingFilterFactory } from '@nimbus-global/features/menu/services/filters/feature-flipping.filter';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { PermissionsModel } from '@nimbus-global/features/permissions/models/permissions.model';
import { GlobalPermissionsModelFilter } from '@nimbus-global/features/menu/services/filters/global-permissions-model.filter';

@Injectable()
export class MenuProviderService extends IMenuProviderService {
  private readonly roleNimbusAdminFilter = RoleFilter.for({ permissionSet: NimbusPermissionSet.NimbusAdmin });
  private readonly roleEveryoneExceptOUAdminFilter = RoleFilter.for({
    permissionSet: [
      NimbusPermissionSet.TenantUser,
      NimbusPermissionSet.AuthorizedUser,
      NimbusPermissionSet.ParentOuAdmin,
      NimbusPermissionSet.OuAdmin,
      NimbusPermissionSet.ChildOuAdmin,
      NimbusPermissionSet.NimbusUser,
    ],
    ignoreTeamSelection: true,
    negate: true,
  });

  private readonly pageOperationsFilter = CombineFilter.for({
    filters: [
      this.roleNimbusAdminFilter,
      PermissionFilter.for({ permission: NimbusPermission.ViewAdminOperationsServicesMarker }),
      PermissionFilter.for({ permission: NimbusPermission.ViewAdminOperationsCustomersMarker }),
      PermissionFilter.for({ permission: NimbusPermission.ViewAdminOperationsChangeHistoryMarker }),
      PermissionFilter.for({ permission: NimbusPermission.ViewAdminOperationsTaskManagementMarker }),
      PermissionFilter.for({ permission: NimbusPermission.ViewAdminOperationsFeatureFlagVisibilityMarker }),
    ],
    condition: 'or',
  });
  private readonly pageDedicatedTenantsFilter = PermissionFilter.for({
    permission: NimbusPermission.ManageDedicatedTenants,
    ignoreTeamSelection: true,
  });
  private readonly pageTenantFilter = CombineFilter.for({
    filters: [
      PermissionFilter.for({ permission: NimbusPermission.ManageTenant, ignoreTeamSelection: true }),
      ExcludePermissionFilter.for({ permission: NimbusPermission.ManageDedicatedTenants, ignoreTeamSelection: false }),
    ],
    condition: 'and',
  });
  private readonly pageTenantsServiceFilter = CombineFilter.for({
    filters: [
      RoleFilter.for({ permissionSet: NimbusPermissionSet.OuAdmin, ignoreTeamSelection: true }),
      RoleFilter.for({ permissionSet: NimbusPermissionSet.WorkflowAdminLimited, ignoreTeamSelection: true }),
      PermissionFilter.for({ permission: NimbusPermission.ServicesRead, ignoreTeamSelection: false }),
    ],
    condition: 'or',
  });
  private readonly pageTenantsUserFilter = CombineFilter.for({
    filters: [
      RoleFilter.for({ permissionSet: NimbusPermissionSet.OuAdmin, ignoreTeamSelection: true }),
      PermissionFilter.for({ permission: NimbusPermission.UsersRead, ignoreTeamSelection: true }),
    ],
    condition: 'or',
  });
  private readonly pageConfigurationFilter = PermissionFilter.for({
    permission: NimbusPermission.ViewConfigurationMarker,
    ignoreTeamSelection: true,
  });
  private readonly pageLicensingFilter = RoleFilter.for({
    permissionSet: [
      NimbusPermissionSet.NimbusAdmin,
      NimbusPermissionSet.PartnerAdmin,
      NimbusPermissionSet.TenantAdmin,
      NimbusPermissionSet.PartnerAdminForTenant,
      NimbusPermissionSet.PartnerAdminForAllTenants,
    ],
    ignoreTeamSelection: false,
  });

  private readonly mapper: MenuMapper;

  public constructor(
    private readonly injector: Injector,
    private readonly featureFlippingService: FeatureFlippingService,
    protected readonly permissionManagerService: IPermissionManagerService,
  ) {
    super();
    this.mapper = new MenuMapper();
  }

  public getMenu(): Observable<MenuViewModel> {
    const featureFlippingFilterFactory = new FeatureFlippingFilterFactory(this.featureFlippingService);

    const mainMenu: MenuStructure = {
      entries: [
        {
          icon: ICONS.HOME,
          route: Routes.Home,
          filters: [
            this.roleEveryoneExceptOUAdminFilter,
            PermissionFilter.for({ permission: NimbusPermission.ReadTenantsOverview, ignoreTeamSelection: true }),
          ],
        },
        { icon: ICONS.HANDSHAKE, route: Routes.Partners, filters: [this.roleNimbusAdminFilter] },
        { icon: ICONS.TENANT, route: Routes.Tenants, filters: [this.pageDedicatedTenantsFilter] },
        { icon: ICONS.TENANT, route: Routes.TenantSettings, filters: [this.pageTenantFilter] },
        { icon: ICONS.USERS, route: Routes.Teams, filters: [this.pageTenantsServiceFilter], connectedPaths: [Routes.Settings] },
        { icon: ICONS.USER, route: Routes.Users, filters: [this.pageTenantsUserFilter] },
        {
          icon: ICONS.LICENSING_PAGE,
          route: Routes.Licensing,
          filters: [this.pageLicensingFilter],
        },
        { icon: ICONS.COG, route: Routes.TeamManage, filters: [this.pageConfigurationFilter] },
        {
          icon: ICONS.OPERATOR,
          route: { name: TranslationKeys.MenuOperations },
          subClass: 'sub-menu-level-1',
          filters: [this.pageOperationsFilter],
          subEntries: [
            { icon: ICONS.USERS, route: Routes.Service, filters: [this.pageOperationsFilter] },
            { icon: ICONS.OPERATOR_CUSTOMER, route: Routes.Customer, filters: [this.pageOperationsFilter] },
            {
              icon: ICONS.CHANGE_HISTORY,
              route: Routes.ChangeHistory,
              filters: [
                this.pageOperationsFilter,
                featureFlippingFilterFactory.forFeature(Feature.ShowChangeHistoryOnUi),
                {
                  validate$: (): Observable<boolean> =>
                    this.permissionManagerService
                      .getPermissionsModel$()
                      .pipe(
                        map((permissionsModel: PermissionsModel) =>
                          permissionsModel.tenantIds.some((id) =>
                            permissionsModel.isGrantedForTenant(id, NimbusPermission.ChangeHistoryRead),
                          ),
                        ),
                      ),
                },
              ],
            },
            {
              icon: ICONS.LAYER_GROUP_LIGHT,
              route: Routes.TaskManagement,
              filters: [this.pageOperationsFilter],
            },
            {
              icon: ICONS.TAGS,
              route: Routes.FeatureFlagVisibility,
              filters: [this.roleNimbusAdminFilter],
            },
            { icon: ICONS.TARGET, route: Routes.ExternalTools, filters: [this.roleNimbusAdminFilter] },
            { icon: ICONS.TASKS, route: Routes.OperationsManage, filters: [this.roleNimbusAdminFilter] },
          ],
        },
        { icon: ICONS.USER_SETTINGS, route: Routes.UserPreferences, renderAs: MenuEntryType.UserPreferences, filters: [] },
        {
          icon: ICONS.COMPANION,
          route: Routes.CompanionConversation,
          renderAs: MenuEntryType.Companion,
          filters: [
            featureFlippingFilterFactory.forFeatureInFeatureManagement(Feature.CompanionConversation),
            new GlobalPermissionsModelFilter(this.permissionManagerService, (model) => model?.tenant?.allowCompanionConversation ?? false),
          ],
        },
      ],
      footerEntries: [],
      brand: { icon: ICONS.BRAND, filters: [], route: Routes.Brand },
      filters: [AuthenticationFilter, NavigationFilter],
      separatorPositions: [Routes.TeamManage.name, Routes.CompanionConversation.name],
    };

    return of(this.mapper.handle(mainMenu, this.injector));
  }
}
